import {BaseModel} from '../base/BaseModel.js';
import moment from 'moment';
import {UnitConverter} from '../../utils/UnitConverter';

/**
 * @class CropSeasonModel
 * @extends BaseModel
 *
 * @property {object} attributes
 * @property {string<UUID>} attributes.uuid
 * @property {string<UUID>} attributes.cropUuid
 * @property {string<UUID>} attributes.varietyUuid
 * @property {string<UUID>} attributes.tillageUuid
 * @property {string<UUID>} attributes.preCropUuid
 * @property {string<UUID>} attributes.fieldUuid
 * @property {string<UUID>} attributes.cropUsageUuid
 * @property {Number} attributes.yieldExpectation
 * @property {Number} attributes.yield
 * @property {Number} attributes.isActive
 * @property {Moment} attributes.startDate
 * @property {Moment} attributes.endDate
 */
export class CropSeasonModel extends BaseModel {

    get minYield () {

        return this._minYield;
    }

    get maxYield () {

        return this._maxYield;
    }

    get avgYield () {

        return this._avgYield;
    }

    get yieldUnit () {

        return this._yieldUnit;
    }

    set minYield (minYield) {

        this._minYield = minYield;
    }

    set maxYield (maxYield) {

        this._maxYield = maxYield;
    }

    set avgYield (avgYield) {

        this._avgYield = avgYield;
    }

    set yieldUnit (yieldUnit) {

        this._yieldUnit = yieldUnit;
    }

    initialize () {

        this.microservice = 'farms';
        this.resource = 'crop-seasons';
    }

    /**
     * @description validates properties of the model
     *
     * @param property
     * @param attributes
     */
    validateProperty (property, attributes) {

        switch (property) {

            case 'yieldExpectation': {

                let error = this.validateYieldExpectation(attributes.yieldExpectation);
                
                if (error) {

                    switch (error.type) {
                        // Will set the yield expectation to the average of max and min
                        case 1:
                            this.set('yieldExpectation', this.avgYield);
                            break;
                        // Will set the yield expectation to min if it is less than min
                        case 2:
                            this.set('yieldExpectation', this.maxYield);
                            break;
                        // Will set the yield expectation to max if it is more than than max
                        case 3:
                            this.set('yieldExpectation', this.minYield);
                            break;
                        default:
                            return error;
                    }
                } else {
                    this.set('yieldExpectation', Number(attributes.yieldExpectation));
                }
            }

        }
    }

    /**
     * @description validates the yield expectation value
     *              should accept only numbers with or without decimals
     *
     * @returns {object} Error message, type and property name
     *          type 1 if yield expectation is not a number
     *          type 2 if yield expectation is more than maxYield
     *          type 3 if yield expectation is less than minYield
     */
    validateYieldExpectation (yieldExpectation) {

        yieldExpectation = Number(yieldExpectation);

        if (_.isNaN(yieldExpectation)) {

            return {
                property: 'yieldExpectation',
                message: __('YIELD_EXPECTATION_INVALID'),
                type : 1
            };
        }

        if (yieldExpectation > this.maxYield) {

            return {
                property: 'yieldExpectation',
                message: __('YIELD_EXPECTATION_INVALID'),
                type : 2
            };
        }

        if (yieldExpectation < this.minYield){

            return {
                property: 'yieldExpectation',
                message: __('YIELD_EXPECTATION_INVALID'),
                type : 3
            };
        }

        return undefined;
    }

    /**
     * @description To check if all the attributes are valid before creating a cropseason
     *
     * @param {Object} attrs - All the attributes of the model.
     * @returns {boolean} valid of invalid
     */
    isValidForSave (attrs) {

        attrs = attrs || this.attributes;

        try {

            if (!_.isNumber(attrs.yieldExpectation)) {
                return false;
            } 
            if (attrs.yieldExpectation > this.maxYield) {
                return false;
            } 
            if (attrs.yieldExpectation < this.minYield) {
                return false;
            } 
            if (!attrs.startDate.isValid()) {
                return false;
            }
            if (!_.isString(attrs.cropUuid)) {
                return false;
            }
            if (!_.isString(attrs.varietyUuid)) {
                return false;
            }
            if (!_.isString(attrs.tillageUuid) && !_.isNull(attrs.tillageUuid)) {
                return false;
            }
            if (!_.isString(attrs.preCropUuid) && !_.isNull(attrs.preCropUuid)) {
                return false;
            }
            if (attrs.endDate && _.isNil(attrs.yield)) {
                return false;
            }
            if (attrs.yield && _.isNil(attrs.endDate)) {
                return false;
            }
            if (attrs.endDate && attrs.endDate.isAfter(BaseModel.getLocalMoment(), 'days')) {
                return false;
            }
            if (attrs.endDate && attrs.startDate && attrs.endDate.isSameOrBefore(attrs.startDate, 'days')) {
                return false;
            }
            if (!_.isString(attrs.fieldUuid)){
                return false;
            }
        } catch (error) {
            return false;
        }

        return true;
    }

    parse (response) {

        if (response) {

            if (_.isString(response.startDate)) {
                response.startDate = BaseModel.parseDate(response.startDate);
            }

            if (_.isString(response.endDate)) {
                response.endDate = BaseModel.parseDate(response.endDate);
            }
        }

        return response;
    }

    toJSON () {

        let obj = _.clone(this.attributes);

        if (obj.startDate) {
            obj.startDate = BaseModel.serializeDate(obj.startDate);
        }

        if (obj.yieldExpectation) {
            obj.yieldExpectation = UnitConverter.localToSi(obj.yieldExpectation, this.yieldUnit);
        }

        if (obj.yield) {
            obj.yield = UnitConverter.localToSi(obj.yield, this.yieldUnit);
        }

        if (_.isUndefined(obj.cropUuid) && !_.isUndefined(obj.selectedCrop)) {
            obj.cropUuid = obj.selectedCrop;
        }

        if (_.isUndefined(obj.varietyUuid) && !_.isUndefined(obj.selectedVariety)) {
            obj.varietyUuid = obj.selectedVariety;
        }

        delete obj._dateMinValue;

        return obj;
    }

    isPlanned() {

        return !this.attributes.isActive && (this.attributes.startDate) && !(this.attributes.endDate);
    }

    isHarvestPossible() {
        return this.get('startDate').isBefore(BaseModel.getLocalMoment(), 'day') && this.get('isActive');
    }

    isGSDescriptionVisible() {
        return this.get('startDate').isSameOrBefore(BaseModel.getLocalMoment(), 'day') && this.get('isActive');
    }

    isFDLUploadAllowed() {
        return moment(this.attributes.startDate).isSameOrBefore(BaseModel.getLocalMoment(), 'day');
    }

    /**
     * @param {VarietyCollection} varieties
     * @returns {undefined | VarietyModel}
     */
    getCropVariety ({varieties}) {
        if (!varieties) {
            return;
        }

        return varieties.get(this.get('varietyUuid'));
    }

    /**
     * @param {VarietyCollection} varieties
     * @returns {undefined | string}
     */
    getCropVarietyName ({varieties}) {
        const cropVariety = this.getCropVariety({varieties});

        return cropVariety && cropVariety.get('name');
    }

    /**
     * @param {CropCollection} crops
     * @returns {undefined | CropModel}
     */
    getCrop ({crops}) {
        if (!crops) {
            return;
        }

        return crops.get(this.get('cropUuid'));
    }

    /**
     * @param {CropCollection} crops
     * @returns {undefined | string}
     */
    getCropName ({crops}) {
        const crop = this.getCrop({crops});

        return crop && crop.get('name');
    }

    /**
     * @param {CropCollection} crops
     * @returns {undefined | string}
     */
    getCropIcon ({crops}) {
        const crop = this.getCrop({crops});

        return crop && crop.getIcon();
    }


    static parseGQL(data) {
        const {
            uuid,
            yield: yieldd, // To avoid yield keyword
            yieldExpectation,
            preCropUuid,
            startDate,
            endDate,
            isActive,
            field,
            tillageUuid,
            tillageSystem,
            crop,
            variety,
            cropUsage,
            isHealthyField
        } = data;

        return {
            uuid,
            yield: yieldd, // to avoid yield keyword
            yieldExpectation,
            preCropUuid,
            isHealthyField,
            startDate: moment(startDate),
            endDate: moment(endDate),
            isActive,
            fieldUuid: field && field.uuid || undefined,
            tillageUuid: (tillageUuid || (tillageSystem ? tillageSystem.uuid : null)),
            cropUuid: crop && crop.uuid || undefined,
            varietyUuid: variety && variety.uuid || undefined,
            cropUsageUuid: cropUsage && cropUsage.uuid || undefined,
        };
    }
}



// WEBPACK FOOTER //
// ./shared/model/farms/CropSeasonModel.js